<script lang="ts" setup>
  import PanelTitled from '@/elements/PanelTitled.vue'
  import ButtonDefault from '@/elements/ButtonDefault.vue'
  import { useGeneratedConfigStore } from '@/stores'
  import { ConfigJsonldDefaultRead } from '@/api'
  import Message from 'primevue/message'
  import { useFeatureFlags } from '@/composables/useFeatureFlag.ts'

  interface PasswordComplexityObject {
    min_digits: number;
    min_length: number;
    min_lowercase: number;
    min_special: number;
    min_uppercase: number;
    max_fails: number;
  }

  const selectOptions = [
    { value: { min_length: 6, min_uppercase: 0, min_lowercase: 0, min_digits: 0, min_special: 0, max_fails: 10 }, label: 'Niedrig' },
    { value: { min_length: 8, min_uppercase: 1, min_lowercase: 1, min_digits: 1, min_special: 0, max_fails: 5 }, label: 'Mittel' },
    { value: { min_length: 12, min_uppercase: 1, min_lowercase: 1, min_digits: 1, min_special: 1, max_fails: 3 }, label: 'Hoch' }
  ]

  const configGenStore = useGeneratedConfigStore()
  const toast = useToastSimplified()
  const featureFlags = useFeatureFlags()

  const isLoading = ref<boolean>(true)

  const passConfig = ref<ConfigJsonldDefaultRead>()
  const passConfigObject = ref<PasswordComplexityObject>()
  const selectValue = ref()

  onMounted(async () => {
    await refresh()
    isLoading.value = false
  })

  const onSubmitClick = async () => {
    if (!selectValue.value) return
    await configGenStore.putId({
      path: { id: String(passConfig.value?.id!) },
      body: { value: JSON.stringify(selectValue.value) }
    })
    await refresh()
    toast.addPredefined('saveConfirmation')
  }

  const refresh = async () => {
    passConfig.value = await configGenStore.find({ query: { key: 'password_complexity' } }, { forceRefresh: true }).then(d => d.value)
    passConfigObject.value = JSON.parse((passConfig.value?.value as string) ?? '{}') ?? {}
    selectValue.value = JSON.parse((passConfig.value?.value as string) ?? '{}') ?? {}
  }
</script>

<template>
  <div v-if="!featureFlags.isEnabled('password_complexity')">
    <Message severity="error">Die Kennwortkomplexität ist deaktiviert</Message>
  </div>
  <div v-else>
    <Message severity="error" v-if="!passConfig && !isLoading" class="w-full">
      Die Konfigurationsobjekt kann nicht gefunden werden.
      Möglicherweise muss zunächst ein Eintrag in die Datenbank vorgenommen werden.
      (Tabelle: Configs, Spalte: 'password_complexity')
    </Message>
    <div class="w-full md:flex">
      <PanelTitled title="Aktuelle Einstellungen" class="w-full mr-0 md:mr-1 mb-2 md:mb-0">
        <ul class="space-y-2">
          <li class="flex items-center">
            <span class="mr-2">[Len]</span>
            <span>Mindestlänge: <strong>{{ passConfigObject?.min_length ?? 'nicht gesetzt' }} </strong></span>
          </li>
          <li class="flex items-center">
            <span class="mr-2">[AÖФ]</span>
            <span>Mindestens Großbuchstaben: <strong>{{ passConfigObject?.min_uppercase ?? 'nicht gesetzt' }}</strong></span>
          </li>
          <li class="flex items-center">
            <span class="mr-2">[aöф]</span>
            <span>Mindestens Kleinbuchstaben: <strong>{{ passConfigObject?.min_lowercase ?? 'nicht gesetzt' }}</strong></span>
          </li>
          <li class="flex items-center">
            <span class="mr-2">[0-9]</span>
            <span>Mindestens Ziffern: <strong>{{ passConfigObject?.min_digits ?? 'nicht gesetzt' }}</strong></span>
          </li>
          <li class="flex items-center">
            <span class="mr-2">[@#$%]</span>
            <span>Mindestens Sonderzeichen: <strong>{{ passConfigObject?.min_special ?? 'nicht gesetzt' }}</strong></span>
          </li>
          <li class="flex items-center">
            <span class="mr-2">[Lock]</span>
            <span>Maximale Fehlversuche: <strong>{{ passConfigObject?.max_fails ?? 'nicht gesetzt' }}</strong></span>
          </li>
        </ul>
      </PanelTitled>
      <PanelTitled title="Einstellungen bearbeiten" class="w-full ml-0 md:ml-1">
        <div class="md:flex">
          <FloatLabel variant="on" class="w-full md:w-1/2">
            <Select
              v-model="selectValue"
              :options="selectOptions"
              option-value="value"
              option-label="label"
              class="w-full" />
            <label>Passwortkomplexität</label>
          </FloatLabel>
          <div class="w-full md:w-1/2 md:ml-2 text-sm mt-2 md:mt-0" v-if="Object.keys(selectValue ?? {}).length">
            <ul class="space-y-2">
              <li class="flex items-center">
                <span class="mr-2">[Len]</span>
                <span>Mindestlänge: <strong>{{ selectValue?.min_length }}</strong></span>
              </li>
              <li class="flex items-center">
                <span class="mr-2">[AÖФ]</span>
                <span>Mindestens Großbuchstaben: <strong>{{ selectValue?.min_uppercase }}</strong></span>
              </li>
              <li class="flex items-center">
                <span class="mr-2">[aöф]</span>
                <span>Mindestens Kleinbuchstaben: <strong>{{ selectValue?.min_lowercase }}</strong></span>
              </li>
              <li class="flex items-center">
                <span class="mr-2">[0-9]</span>
                <span>Mindestens Ziffern: <strong>{{ selectValue?.min_digits }}</strong></span>
              </li>
              <li class="flex items-center">
                <span class="mr-2">[@#$%]</span>
                <span>Mindestens Sonderzeichen: <strong>{{ selectValue?.min_special }}</strong></span>
              </li>
              <li class="flex items-center">
                <span class="mr-2">[Lock]</span>
                <span>Maximale Fehlversuche: <strong>{{ selectValue?.max_fails ?? 'nicht gesetzt' }}</strong></span>
              </li>
            </ul>
          </div>
        </div>
        <div class="w-full flex md:justify-end mt-2">
          <ButtonDefault
            class="w-full md:w-auto"
            icon="SubmitIcon"
            label="Absenden"
            outlined
            size="small"
            :disabled="!Object.keys(selectValue ?? {}).length
          || JSON.stringify(selectValue ?? {}) === JSON.stringify(passConfigObject ?? {})
          || !passConfig"
            @click="onSubmitClick" />
        </div>
      </PanelTitled>
    </div>
  </div>
</template>
